import { Button } from '@/components/ui'
import { AsideCard } from './AsideCard'
import { consultationSidebarStrings } from './strings'

interface PatientLite {
  allergies: string[]
  chronicConditions: string[]
}

interface PrevConsultation {
  id: string
  consultedAt: string
  chiefComplaint: string | null
}

export interface ConsultationSidebarProps {
  isSigned: boolean
  hasProtocols: boolean
  patient: PatientLite | null | undefined
  prevList: PrevConsultation[]
  onAddProtocol: () => void
  onPrevClick: (id: string) => void
  formatDate: (iso: string) => string
}

/**
 * Page-level sidebar shown alongside the consultation protocol panel.
 * Composes alerts, the protocols empty-state card, and the
 * previous-consultations list. The orders queue panel is rendered by the
 * parent rail (OrdersRail) below this sidebar.
 */
export function ConsultationSidebar({
  isSigned,
  hasProtocols,
  patient,
  prevList,
  onAddProtocol,
  onPrevClick,
  formatDate,
}: ConsultationSidebarProps): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      {patient && (patient.allergies.length > 0 || patient.chronicConditions.length > 0) && (
        <AsideCard title={consultationSidebarStrings.patientAlertsTitle}>
          <div className="flex flex-col gap-2">
            {patient.allergies.map((a) => (
              <div
                key={a}
                className="flex gap-3 px-3 py-3 bg-danger-bg border border-danger-border rounded text-xs text-danger-text leading-label"
              >
                <i className="ph ph-x-circle text-body-lg shrink-0 mt-1" />
                <div>
                  <strong>{consultationSidebarStrings.allergyPrefix}</strong> · {a}
                </div>
              </div>
            ))}
            {patient.chronicConditions.map((c) => (
              <div
                key={c}
                className="flex gap-3 px-3 py-3 bg-warning-bg border border-warning-border rounded text-xs text-warning-text leading-label"
              >
                <i className="ph ph-warning-circle text-body-lg shrink-0 mt-1" />
                <div>{c}</div>
              </div>
            ))}
          </div>
        </AsideCard>
      )}

      {!hasProtocols && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-overline font-mono uppercase tracking-label text-n-400">
              {consultationSidebarStrings.protocolsLabel}
            </span>
            {!isSigned && (
              <Button variant="secondary" size="sm" onClick={onAddProtocol}>
                <i className="ph ph-plus text-xs" />
                {consultationSidebarStrings.addProtocolButton}
              </Button>
            )}
          </div>
          <div className="flex flex-col items-center gap-2 py-6 border border-dashed border-n-200 rounded-md text-center">
            <i className="ph ph-stack text-h3 text-n-400" />
            <p className="text-xs text-n-400">
              {isSigned
                ? consultationSidebarStrings.noProtocolsSigned
                : consultationSidebarStrings.noProtocolsUnsigned}
            </p>
          </div>
        </div>
      )}

      {prevList.length > 0 && (
        <AsideCard title={consultationSidebarStrings.prevConsultationsTitle}>
          <div className="flex flex-col gap-1">
            {prevList.map((c) => (
              <Button
                key={c.id}
                variant="item"
                size="sm"
                onClick={() => onPrevClick(c.id)}
                className="flex items-center justify-between w-full text-left py-2"
              >
                <span className="truncate flex-1 text-left">
                  {c.chiefComplaint ?? consultationSidebarStrings.noChiefComplaint}
                </span>
                <span className="font-mono text-n-400 text-overline shrink-0 ml-2">
                  {formatDate(c.consultedAt)}
                </span>
              </Button>
            ))}
          </div>
        </AsideCard>
      )}
    </div>
  )
}
